import React from 'react';
import { useRouter } from 'next/router';

import { useAuthContext } from '../../../context/Auth';

import { SharedProps, WidgetProps } from '../shared';

const CF_COLORS: Record<string, string> = {
  spearmint: '#80D6B0',
  green: '#3BBFAD',
  ginger: '#F4BA42',
  blackberry: '#1D252C',
  cinnamint: '#D66755',
  sweetmint: '#035C67',
  white: '#FFFFFF',
  mutedNavy: '#9AB5B8',
  transparent: 'transparent',
  limegreen: '#BBF174',
  callout: '#CDE012'
};

function getTokenKey(token: unknown): keyof typeof CF_COLORS | undefined {
  if (!token) return undefined;
  if (typeof token === 'string') return token as keyof typeof CF_COLORS;
  if (typeof token === 'object') {
    const obj = token as Record<string, string | undefined>;
    return (
      (obj['0'] as keyof typeof CF_COLORS | undefined) ||
      (obj.min as keyof typeof CF_COLORS | undefined) ||
      (obj.mobile as keyof typeof CF_COLORS | undefined) ||
      (obj.tablet as keyof typeof CF_COLORS | undefined) ||
      (obj.desktop as keyof typeof CF_COLORS | undefined) ||
      (obj.max as keyof typeof CF_COLORS | undefined)
    );
  }
  return undefined;
}

function resolveBg(token: unknown, fallback: string) {
  const key = getTokenKey(token);
  return key && CF_COLORS[key] ? CF_COLORS[key] : fallback;
}

function resolveText(token: unknown) {
  const dark = new Set(['blackberry', 'sweetmint']);
  const key = getTokenKey(token);
  return key && dark.has(key) ? '#FFFFFF' : '#1D252C';
}

export function InlineAuthWidget({
  buttonColor = 'ginger',
  buttonHoverColor = 'green',
  buttonText = 'Sign up'
}: WidgetProps & SharedProps) {
  const { isAuthenticated } = useAuthContext();
  const router = useRouter();

  return (
    <div className="flex">
      {!isAuthenticated ? (
        <button
          role="link"
          aria-label="Sign up for Greenlight for Classrooms"
          data-testid="inline-signUp-btn"
          onClick={() => router.push('/classrooms/lesson-library/u/register')}
          className="relative z-[1] h-[45px] min-h-[45px] min-w-[110px] rounded-[8px] border-0 px-3 text-[18px] font-medium outline-0 transition-all duration-300 ease-[cubic-bezier(0,0.7,0.3,1.01)] desktop:h-[56px] desktop:min-h-[36px]"
          style={{
            WebkitTapHighlightColor: 'transparent',
            willChange: 'width, flex-grow',
            backgroundColor: resolveBg(buttonColor, '#F4BA42'),
            color: resolveText(buttonColor)
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = resolveBg(
              buttonHoverColor,
              resolveBg(buttonColor, '#F4BA42')
            );
            e.currentTarget.style.color = resolveText(buttonHoverColor);
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = resolveBg(
              buttonColor,
              '#F4BA42'
            );
            e.currentTarget.style.color = resolveText(buttonColor);
          }}
        >
          {buttonText}
        </button>
      ) : (
        <button
          role="link"
          aria-label="Go to Greenlight for Classrooms Lesson Library"
          data-testid="go-to-library-btn"
          onClick={() => router.push('/classrooms/lesson-library')}
          className="relative z-[1] h-[45px] min-h-[45px] min-w-[110px] rounded-[8px] border-0 px-3 text-[18px] font-medium outline-0 transition-all duration-300 ease-[cubic-bezier(0,0.7,0.3,1.01)] desktop:h-[56px] desktop:min-h-[36px]"
          style={{
            WebkitTapHighlightColor: 'transparent',
            willChange: 'width, flex-grow',
            backgroundColor: resolveBg(buttonColor, '#F4BA42'),
            color: resolveText(buttonColor)
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = resolveBg(
              buttonHoverColor,
              resolveBg(buttonColor, '#F4BA42')
            );
            e.currentTarget.style.color = resolveText(buttonHoverColor);
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = resolveBg(
              buttonColor,
              '#F4BA42'
            );
            e.currentTarget.style.color = resolveText(buttonColor);
          }}
        >
          <p className="typography-body-2">Go to library</p>
        </button>
      )}
    </div>
  );
}
